import React, { useContext } from 'react';

import { IconButton, Tooltip, CircularProgress, Stack } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';

import { ExportJob, ExportStatus } from '../../../types/Exports';

export type ExportActionKind = 'download' | 'email' | 'delete';

// Maps export job id -> the action currently in flight for that job.
// Provided by the Downloads page so busy state is tied to the data (by id),
// not to the row's position in the table. This is what prevents the loader
// from "jumping" to another row after a delete/refetch.
export const ExportBusyContext = React.createContext<Record<string, ExportActionKind>>({});

interface Props {
  job: ExportJob;
  onDownload: (job: ExportJob) => void;
  onEmail: (job: ExportJob) => void;
  onDelete: (job: ExportJob) => void;
}

const ExportRowActions = ({ job, onDownload, onEmail, onDelete }: Props): JSX.Element => {
  const busyMap = useContext(ExportBusyContext);
  const busyKind = busyMap[job.id] ?? null;
  const busy = busyKind !== null;
  const ready = job.isReady || job.status === ExportStatus.Completed;

  return (
    <Stack direction='row' spacing={0.5} justifyContent='flex-end'>
      <Tooltip title={ready ? 'Download' : 'Not ready yet'}>
        <span>
          <IconButton size='small' color='primary' disabled={!ready || busy} onClick={() => onDownload(job)}>
            {busyKind === 'download' ? <CircularProgress size={18} /> : <DownloadIcon fontSize='small' />}
          </IconButton>
        </span>
      </Tooltip>

      {/* We'll use it if Required */}
      {/* <Tooltip title={ready ? 'Email to me' : 'Not ready yet'}>
        <span>
          <IconButton size='small' disabled={!ready || busy} onClick={() => onEmail(job)}>
            {busyKind === 'email' ? <CircularProgress size={18} /> : <EmailOutlinedIcon fontSize='small' />}
          </IconButton>
        </span>
      </Tooltip> */}

      <Tooltip title='Delete'>
        <span>
          <IconButton size='small' color='error' disabled={busy} onClick={() => onDelete(job)}>
            {busyKind === 'delete' ? <CircularProgress size={18} /> : <DeleteOutlineIcon fontSize='small' />}
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
};

export default ExportRowActions;
